"use client";


import { Statistics, Publication, Author, AnalysisMeta } from "@/types/types";
import { saveAs } from "file-saver";
import { PiMicrosoftExcelLogo, PiMicrosoftWordLogo } from "react-icons/pi";
import { FaFileExport, FaFileCsv } from "react-icons/fa";
import { useState, useEffect, useRef } from "react";
import { useTranslations } from 'next-intl';
import { ExportHeader, ExportHeaderSelections } from "@/types/types";
import { IoCheckmarkCircle, IoClose } from 'react-icons/io5';

interface ExportButtonProps {
  author: Author;
  stats: Statistics | null;
  publications: Publication[];
  meta: AnalysisMeta;
}

interface BasicData {
  name: string;
  mtmtId: string;
  homepage: string;
  mtmtProfile: string;
  lastUpdate: string;
  ifListVersionYear: number | null;
  norwayListVersionYear: number | null;
  excludeNorwayLevel0: boolean;
  totalStats: {
    publications: number;
    scientificCitations: number;
    hIndex: number;
    iScore: number;
    iScoreWOS: number;
  };
  impactFactorStats: {
    total: number;
    relative: number;
    publicationsWithIF: number;
  };
  qScores: {
    total: number;
    fromArticles: number;
    fromBooks: number;
  };
}

const getBasicData = (author: Author, stats: Statistics, t: any, meta: AnalysisMeta): BasicData => ({
  name: author.name,
  mtmtId: author.mtid,
  homepage: author.identifiers?.find(id => 
    id.source.label === "Homepage" || id.source.label === "Honlap"
  )?.realUrl || "",
  mtmtProfile: `https://m2.mtmt.hu/api/author/${author.mtid}`,
  lastUpdate: meta.lastUpdated,
  ifListVersionYear: meta.ifListVersionYear ?? null,
  norwayListVersionYear: meta.norwayListVersionYear ?? null,
  excludeNorwayLevel0: meta.excludeNorwayLevel0,
  totalStats: {
    publications: stats.totalScientificPublications,
    scientificCitations: stats.totalScientificCitations,
    hIndex: stats.hIndex,
    iScore: Number(stats.iScore.toFixed(3)),
    iScoreWOS: Number(stats.iScoreWOS.toFixed(3))
  },
  impactFactorStats: {
    total: Number(stats.impactFactor.total.toFixed(3)),
    relative: Number(stats.impactFactor.relative.toFixed(3)),
    publicationsWithIF: stats.publicationsWithIF
  },
  qScores: {
    total: Number(stats.qScore.total.toFixed(3)),
    fromArticles: Number(stats.qScore.fromArticles.toFixed(3)),
    fromBooks: Number(stats.qScore.fromBooks.toFixed(3))
  }
});

// Define color constants
const COLORS = {
  GREEN: "16A085",  // from basicData Q-Scores
  RED: "E74C3C",    // from basicData Statistics
  YELLOW: "F39C12", // from basicData I-Score
  ORANGE: "E67E22",  // from basicData orange
  BLUE: "3498DB",   // from basicData Impact Factor
  PURPLE: "8E44AD", // from basicData H-Index
  DARK: "2C3E50",   // from basicData headers
  GRAY: "7F8C8D"    // from basicData values
};

export default function ExportButton({
  author,
  stats,
  publications,
  meta,
}: ExportButtonProps) {
  const t = useTranslations('export');
  const typeT = useTranslations("statistics.publicationTypes");
  const [showExportOptions, setShowExportOptions] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const [showHeaderSelection, setShowHeaderSelection] = useState(false);
  const [exportType, setExportType] = useState<'excel' | 'word' | 'csv'>('excel');
  const defaultHeaders: ExportHeader[] = [
    { key: 'year', label: t('headers.publications.year') },
    { key: 'title', label: t('headers.publications.title') },
    { key: 'authors', label: t('headers.publications.authors') },
    { key: 'journalBook', label: t('headers.publications.journalBook') },
    { key: 'type', label: t('headers.publications.type') },
    { key: 'scientific', label: t('headers.publications.scientific') },
    { key: 'rating', label: t('headers.publications.rating') },
    { key: 'if', label: t('headers.publications.impactFactor') },
    { key: 'qScore', label: t('headers.publications.qScore') },
    { key: 'iScore', label: t('headers.publications.iScore') },
    { key: 'citations', label: t('headers.publications.citations') },
    { key: 'scientificCitations', label: t('headers.publications.scientificCitations') },
    { key: 'share', label: t('headers.publications.share') },
    { key: 'url', label: t('headers.publications.url') },
    { key: 'mtmtId', label: t('headers.publications.mtmtId') },
    { key: 'norwayLevel', label: t('headers.publications.norwayLevel') },
    { key: 'hasISBNISSN', label: t('headers.publications.hasISBNISSN') },
  ];
  
  const [headerSelections, setHeaderSelections] = useState<ExportHeaderSelections>(() => {
    const saved = localStorage.getItem('export-header-selections');
    return saved ? JSON.parse(saved) : {
      excel: defaultHeaders.map(h => h.key),
      word: defaultHeaders.map(h => h.key), 
      csv: defaultHeaders.map(h => h.key),
    };
  });


  useEffect(() => {
    localStorage.setItem('export-header-selections', JSON.stringify(headerSelections));
  }, [headerSelections]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current && 
        buttonRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !buttonRef.current.contains(event.target as Node)
      ) {
        setShowExportOptions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  if (!stats) return null;

  const formatDate = () => {
    const date = new Date();
    return date.toISOString().split("T")[0];
  };

  const getFileName = (type: "excel" | "word" | "csv") => {
    const extension = type === "excel" ? "xlsx" : type === "word" ? "docx" : "csv";
    return `MTMT_${author.name.replace(/\s+/g, "_")}_${formatDate()}.${extension}`;
  };

  const publicationTypeTranslations = {
    journalArticle: typeT("journalArticle"),
    conferenceArticle: typeT("conferenceArticle"),
    bookChapter: typeT("bookChapter"),
    book: typeT("book"),
    patent: typeT("patent"),
    thesis: typeT("thesis"),
    other: typeT("other")
  };

  const getPublicationData = (pub: Publication, header: string, mtmtId: string, t: any) => {
    const dataMap: { [key: string]: any } = {
      year: pub.publishedYear,
      title: pub.title,
      authors: pub.authorships?.map(a => a.author?.label || `${a.familyName} ${a.givenName}`).join(", "),
      journalBook: pub.journal?.label || pub.book?.title || "",
      type: pub.getPublicationType(publicationTypeTranslations),
      scientific: pub.isScientific() ? t('headers.publications.yes') : t('headers.publications.no'),
      rating: pub.getRating(""),
      if: pub.IFindex || "",
      qScore: pub.qscore?.toFixed(3) || "",
      iScore: pub.iscore || "",
      citations: pub.citationCount || 0,
      scientificCitations: pub.scientificCitationCount || 0,
      share: pub.getAuthorShare(mtmtId).toFixed(3),
      url: pub.identifiers?.[0]?.realUrl || "",
      mtmtId: pub.mtid || "",
      hasISBNISSN: pub.hasISBNISSN === false ? t('headers.publications.no') : t('headers.publications.yes'),
      norwayLevel: typeof pub.norwayLevel === 'number' ? pub.norwayLevel : "",
    };
    return dataMap[header];
  };

  const exportToCSV = () => {
    const selectedHeaders = headerSelections.csv;
    
    // If no headers selected, export empty file with just basic info
    if (!selectedHeaders || selectedHeaders.length === 0) {
      const basicData = getBasicData(author, stats, t, meta);
      const rows: string[] = [];
      rows.push(`${t('headers.basicInfo.title')}`);
      rows.push(`${t('headers.basicInfo.name')},"${basicData.name.replace(/"/g, '""')}"`);
      rows.push(`${t('headers.basicInfo.mtmtId')},${basicData.mtmtId}`);
      rows.push(`${t('headers.basicInfo.lastUpdate')},${basicData.lastUpdate}`);
      rows.push(`IFList version,${basicData.ifListVersionYear ?? ''}`);
      rows.push(`NorwayList version,${basicData.norwayListVersionYear ?? ''}`);
      rows.push(`${t('headers.basicInfo.excludeNorwayLevel0')},${basicData.excludeNorwayLevel0 ? t('headers.publications.yes') : t('headers.publications.no')}`);
      const csvContent = rows.join("\n") + "\n";
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      saveAs(blob, getFileName("csv"));
      return;
    }

    const headerLabels = selectedHeaders.map(key => 
      defaultHeaders.find(h => h.key === key)?.label || key
    );
    
    const basicData = getBasicData(author, stats, t, meta);
    const metaRows: string[] = [];
    metaRows.push(`${t('headers.basicInfo.title')}`);
    metaRows.push(`${t('headers.basicInfo.name')},"${basicData.name.replace(/"/g, '""')}"`);
    metaRows.push(`${t('headers.basicInfo.mtmtId')},${basicData.mtmtId}`);
    metaRows.push(`${t('headers.basicInfo.lastUpdate')},${basicData.lastUpdate}`);
    metaRows.push(`IFList version,${basicData.ifListVersionYear ?? ''}`);
    metaRows.push(`NorwayList version,${basicData.norwayListVersionYear ?? ''}`);
    metaRows.push(`${t('headers.basicInfo.excludeNorwayLevel0')},${basicData.excludeNorwayLevel0 ? t('headers.publications.yes') : t('headers.publications.no')}`);
    metaRows.push("");

    let csvContent = metaRows.join("\n") + "\n" + headerLabels.join(",") + "\n";
    
    publications.forEach(pub => {
      const row = selectedHeaders.map(header => {
        const value = getPublicationData(pub, header, author.mtid, t);
        return typeof value === 'string' ? `"${value.replace(/"/g, '""')}"` : value;
      }).join(",");
      csvContent += row + "\n";
    });

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    saveAs(blob, getFileName("csv"));
  };

  const getRatingColor = (rating: string): string => {
    switch (rating) {
      case 'D1':
        return COLORS.GREEN;
      case 'Q1':
        return COLORS.BLUE;
      case 'Q2':
        return COLORS.YELLOW;
      case 'Q3':
        return COLORS.ORANGE;
      case 'Q4':
        return COLORS.RED;
      default:
        return COLORS.GRAY;
    }
  };

  const exportToExcel = async () => {
    const { utils: XLSX, writeFile } = await import("xlsx-js-style");
    const workbook = XLSX.book_new();
    const basicData = getBasicData(author, stats, t, meta);

    // Enhanced Basic Info Sheet with styled sections
    const basicInfo = [
      // Title section with special styling - now spans 2 columns
      [{
        v: t('headers.basicInfo.title'),
        s: {
          font: { bold: true, sz: 16, color: { rgb: "2C3E50" } },
          alignment: { horizontal: "center" },
          fill: { fgColor: { rgb: "ECF0F1" } }
        }
      }, null], // Add null for the second column to be merged
      [""], // Empty row for spacing

      // Author info section
      [
        { v: t('headers.basicInfo.name'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { 
          v: basicData.name,
          l: { Target: basicData.homepage, Tooltip: t('headers.basicInfo.homepage') },
          s: { font: { color: { rgb: "3498DB" }, underline: true } }
        }
      ],
      [
        { v: t('headers.basicInfo.mtmtId'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        {
          v: basicData.mtmtId,
          l: { Target: basicData.mtmtProfile, Tooltip: t('headers.basicInfo.mtmtProfile') },
          s: { font: { color: { rgb: "3498DB" }, underline: true } }
        }
      ],
      [
        { v: t('headers.basicInfo.lastUpdate'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.lastUpdate, s: { font: { color: { rgb: "7F8C8D" } } } }
      ],
      [
        { v: 'IFList version', s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.ifListVersionYear ?? '', s: { font: { color: { rgb: "7F8C8D" } } } }
      ],
      [
        { v: 'NorwayList version', s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.norwayListVersionYear ?? '', s: { font: { color: { rgb: "7F8C8D" } } } }
      ],
      [
        { v: t('headers.basicInfo.excludeNorwayLevel0'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.excludeNorwayLevel0 ? t('headers.publications.yes') : t('headers.publications.no'), s: { font: { color: { rgb: "7F8C8D" } } } }
      ],
      [""], // Empty row for spacing

      // Q Scores section
      [
        { v: t('headers.basicInfo.qScoreTotal'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.qScores.total, s: { font: { color: { rgb: "16A085" } } } }
      ],
      [
        { v: t('headers.basicInfo.qScoreArticles'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.qScores.fromArticles, s: { font: { color: { rgb: "16A085" } } } }
      ],
      [
        { v: t('headers.basicInfo.qScoreBooks'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.qScores.fromBooks, s: { font: { color: { rgb: "16A085" } } } }
      ],
      [""], // Empty row for spacing
      // I-Score section
      [
        { v: t('headers.basicInfo.iScore'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.totalStats.iScore, s: { font: { color: { rgb: "F39C12" } } } }
      ],
      [
        { v: t('headers.basicInfo.wosIScore'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.totalStats.iScoreWOS, s: { font: { color: { rgb: "F39C12" } } } }
      ],
      [""], // Empty row for spacing


      // Impact Factor section
      [
        { v: t('headers.basicInfo.totalIF'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.impactFactorStats.total, s: { font: { color: { rgb: "3498DB" } } } }
      ],
      [
        { v: t('headers.basicInfo.relativeIF'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.impactFactorStats.relative, s: { font: { color: { rgb: "3498DB" } } } }
      ],
      [
        { v: t('headers.basicInfo.publicationsWithIF'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.impactFactorStats.publicationsWithIF, s: { font: { color: { rgb: "3498DB" } } } }
      ],
      [""], // Empty row for spacing


      // H-Index section
      [
        { v: t('headers.basicInfo.hIndex'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.totalStats.hIndex, s: { font: { color: { rgb: "8E44AD" } } } }
      ],
      [""], // Empty row for spacing

      // Basic Statistics section
      [
        { v: t('headers.basicInfo.totalPublications'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.totalStats.publications, s: { font: { color: { rgb: "E74C3C" } } } }
      ],
      [
        { v: t('headers.basicInfo.totalScientificCitations'), s: { font: { bold: true, color: { rgb: "2C3E50" } } } },
        { v: basicData.totalStats.scientificCitations, s: { font: { color: { rgb: "E74C3C" } } } }
      ]
    ];

    // Set column widths
    const ws = XLSX.aoa_to_sheet(basicInfo);
    ws['!cols'] = [{ wch: 30 }, { wch: basicData.name.length * 0.83 }];

    // Add merge for the title cell
    if (!ws['!merges']) ws['!merges'] = [];
    ws['!merges'].push({ 
      s: { r: 0, c: 0 }, // Start at first row, first column
      e: { r: 0, c: 1 }  // End at first row, second column
    });

    // Add the worksheet to workbook
    XLSX.book_append_sheet(workbook, ws, t('headers.basicInfo.title'));

    // Publications Sheet - only create if headers are selected
    const selectedHeaders = headerSelections.excel;
    if (selectedHeaders && selectedHeaders.length > 0) {
      const headerStyle = {
        font: { bold: true, color: { rgb: "FFFFFF" } },
        fill: { fgColor: { rgb: "2C3E50" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "B2BEB5" } },
          bottom: { style: "thin", color: { rgb: "B2BEB5" } },
          left: { style: "thin", color: { rgb: "B2BEB5" } },
          right: { style: "thin", color: { rgb: "B2BEB5" } }
        }
      };

      const cellStyle = {
        font: {},
        alignment: { horizontal: "center", vertical: "center" },
        border: {
          top: { style: "thin", color: { rgb: "B2BEB5" } },
          bottom: { style: "thin", color: { rgb: "B2BEB5" } },
          left: { style: "thin", color: { rgb: "B2BEB5" } },
          right: { style: "thin", color: { rgb: "B2BEB5" } }
        }
      };

      // Create headers with styling
      const pubHeaders = defaultHeaders
        .filter(header => selectedHeaders.includes(header.key))
        .map(header => ({
          v: header.label,
          s: headerStyle
        }));

      // Create data rows with styling
      const publicationsData = [
        pubHeaders,
        ...publications.map((pub) => 
          defaultHeaders
            .filter(header => selectedHeaders.includes(header.key))
            .map(header => {
              const value = getPublicationData(pub, header.key, author.mtid, t);
              let style = { ...cellStyle };

              // Add specific styling based on column type
              switch (header.key) {
                case 'rating':
                  style.font = { color: { rgb: getRatingColor(value) } };
                  break;
                case 'scientific':
                  style.font = { color: { rgb: value === t('headers.publications.yes') ? COLORS.GREEN : COLORS.RED } };
                  break;
                case 'url':
                  if (value) {
                    return {
                      v: value,
                      l: { Target: value, Tooltip: t('headers.publications.url') },
                      s: {
                        ...style,
                        font: { color: { rgb: COLORS.BLUE }, underline: true },
                        alignment: { horizontal: "left", vertical: "center" }
                      }
                    };
                  }
                  break;
                case 'mtmtId':
                  if (value) {
                    const mtmtUrl = `https://m2.mtmt.hu/api/publication/${value}`;
                    return {
                      v: value,
                      l: { Target: mtmtUrl, Tooltip: t('headers.publications.mtmtId') },
                      s: {
                        ...style,
                        font: { color: { rgb: COLORS.BLUE }, underline: true },
                        alignment: { horizontal: "left", vertical: "center" }
                      }
                    };
                  }
                  break;
                case 'title':
                case 'authors':
                case 'journalBook':
                  style.alignment = { horizontal: "left", vertical: "center" };
                  break;
              }

              return {
                v: value,
                s: style
              };
            })
        )
      ];

      const pubWs = XLSX.aoa_to_sheet(publicationsData);

      // Enable autofilter and freeze pane for header row
      if (publicationsData.length > 1) {
        pubWs['!autofilter'] = { ref: pubWs['!ref'] || 'A1' };
        pubWs['!freeze'] = { xSplit: 0, ySplit: 1 };
      }

      // Set column widths
      const columnWidths = {
        year: { wch: 8 },
        title: { wch: 50 },
        authors: { wch: 40 },
        journalBook: { wch: 30 },
        type: { wch: 15 },
        scientific: { wch: 12 },
        rating: { wch: 10 },
        if: { wch: 10 },
        qScore: { wch: 13 },
        iScore: { wch: 11 },
        citations: { wch: 13 },
        scientificCitations: { wch: 13 },
        share: { wch: 10 },
        url: { wch: 50 },
        mtmtId: { wch: 15 },
        hasISBNISSN: { wch: 12 },
        norwayLevel: { wch: 12 }
      };

      try {
        pubWs["!cols"] = defaultHeaders
          .filter(header => selectedHeaders.includes(header.key))
          .map(header => columnWidths[header.key as keyof typeof columnWidths] || { wch: 15 });

        XLSX.book_append_sheet(workbook, pubWs, t('headers.publications.Title'));
      } catch (error) {
        console.error('Error setting column widths:', error);
        XLSX.book_append_sheet(workbook, pubWs, t('headers.publications.Title'));
      }
    }

    writeFile(workbook, getFileName("excel"));
  };

  const exportToWord = async () => {
    try {
      const {
        Document,
        Paragraph,
        Table,
        TableRow,
        TableCell,
        HeadingLevel,
        TextRun,
        Packer,
        AlignmentType,
        BorderStyle,
        ExternalHyperlink,
      } = await import("docx");

      const basicData = getBasicData(author, stats, t, meta);

      // Helper function for creating table rows
      const createTableRow = (label: string, value: string | number) =>
        new TableRow({
          children: [
            new TableCell({
              children: [
                new Paragraph({
                  children: [new TextRun({ text: label, bold: true })],
                }),
              ],
              width: { size: 50, type: "pct" },
            }),
            new TableCell({
              children: [new Paragraph({ text: value.toString() })],
              width: { size: 50, type: "pct" },
            }),
          ],
        });

      const doc = new Document({
        sections: [
          {
            properties: {},
            children: [
              // Title
              new Paragraph({
                text: t('headers.basicInfo.title'),
                heading: HeadingLevel.HEADING_1,
                spacing: { after: 300 },
                alignment: AlignmentType.CENTER,
              }),

              // Author name with hyperlink
              new Paragraph({
                children: [
                  new ExternalHyperlink({
                    children: [
                      new TextRun({
                        text: basicData.name,
                        style: "Hyperlink",
                        size: 28,
                      }),
                    ],
                    link: basicData.homepage,
                  }),
                ],
                spacing: { after: 200 },
                alignment: AlignmentType.CENTER,
              }),

              // MTMT ID with hyperlink
              new Paragraph({
                children: [
                  new TextRun({ text: `${t('headers.basicInfo.mtmtId')}: `, bold: true }),
                  new ExternalHyperlink({
                    children: [
                      new TextRun({
                        text: basicData.mtmtId,
                        style: "Hyperlink",
                      }),
                    ],
                    link: basicData.mtmtProfile,
                  }),
                ],
                spacing: { after: 200 },
                alignment: AlignmentType.CENTER,
              }),

              // Last update
              new Paragraph({
                children: [
                  new TextRun({ text: `${t('headers.basicInfo.lastUpdate')}: `, bold: true }),
                  new TextRun(basicData.lastUpdate),
                ],
                spacing: { after: 400 },
                alignment: AlignmentType.CENTER,
              }),

              // Dataset Versions Section
              new Paragraph({
                text: 'Dataset Versions',
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 },
              }),
              new Table({
                width: { size: 100, type: "pct" },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1 },
                  bottom: { style: BorderStyle.SINGLE, size: 1 },
                  left: { style: BorderStyle.SINGLE, size: 1 },
                  right: { style: BorderStyle.SINGLE, size: 1 },
                },
                rows: [
                  createTableRow('IFList version', basicData.ifListVersionYear ?? ''),
                  createTableRow('NorwayList version', basicData.norwayListVersionYear ?? ''),
                  createTableRow(t('headers.basicInfo.excludeNorwayLevel0'), basicData.excludeNorwayLevel0 ? t('headers.publications.yes') : t('headers.publications.no')),
                ],
              }),

              // Q Scores Section
              new Paragraph({
                text: t('headers.basicInfo.qScores'),
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 },
              }),
              new Table({
                width: {
                  size: 100,
                  type: "pct",
                },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1 },
                  bottom: { style: BorderStyle.SINGLE, size: 1 },
                  left: { style: BorderStyle.SINGLE, size: 1 },
                  right: { style: BorderStyle.SINGLE, size: 1 },
                },
                rows: [
                  createTableRow(t('headers.basicInfo.qScoreTotal'), basicData.qScores.total),
                  createTableRow(t('headers.basicInfo.qScoreArticles'), basicData.qScores.fromArticles),
                  createTableRow(t('headers.basicInfo.qScoreBooks'), basicData.qScores.fromBooks),
                ],
              }),

              // I-Score Section
              new Paragraph({
                text: "I-Score",
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 },
              }),
              new Table({
                width: {
                  size: 100,
                  type: "pct",
                },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1 },
                  bottom: { style: BorderStyle.SINGLE, size: 1 },
                  left: { style: BorderStyle.SINGLE, size: 1 },
                  right: { style: BorderStyle.SINGLE, size: 1 },
                },
                rows: [
                  createTableRow(t('headers.basicInfo.iScore'), basicData.totalStats.iScore),
                  createTableRow(t('headers.basicInfo.wosIScore'), basicData.totalStats.iScoreWOS),
                ],
              }),

              // Impact Factor Statistics Section
              new Paragraph({
                text: t('headers.basicInfo.impactFactorStats'),
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 },
              }),
              new Table({
                width: {
                  size: 100,
                  type: "pct",
                },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1 },
                  bottom: { style: BorderStyle.SINGLE, size: 1 },
                  left: { style: BorderStyle.SINGLE, size: 1 },
                  right: { style: BorderStyle.SINGLE, size: 1 },
                },
                rows: [
                  createTableRow(t('headers.basicInfo.totalIF'), basicData.impactFactorStats.total),
                  createTableRow(t('headers.basicInfo.relativeIF'), basicData.impactFactorStats.relative),
                  createTableRow(t('headers.basicInfo.publicationsWithIF'), basicData.impactFactorStats.publicationsWithIF),
                ],
              }),

              // H-Index Section
              new Paragraph({
                text: "H-Index",
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 },
              }),
              new Table({
                width: {
                  size: 100,
                  type: "pct",
                },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1 },
                  bottom: { style: BorderStyle.SINGLE, size: 1 },
                  left: { style: BorderStyle.SINGLE, size: 1 },
                  right: { style: BorderStyle.SINGLE, size: 1 },
                },
                rows: [
                  createTableRow(t('headers.basicInfo.hIndex'), basicData.totalStats.hIndex),
                ],
              }),

              // Basic Statistics Section
              new Paragraph({
                text: t('headers.basicInfo.basicStats'),
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 },
              }),
              new Table({
                width: {
                  size: 100,
                  type: "pct",
                },
                borders: {
                  top: { style: BorderStyle.SINGLE, size: 1 },
                  bottom: { style: BorderStyle.SINGLE, size: 1 },
                  left: { style: BorderStyle.SINGLE, size: 1 },
                  right: { style: BorderStyle.SINGLE, size: 1 },
                },
                rows: [
                  createTableRow(t('headers.basicInfo.totalPublications'), basicData.totalStats.publications),
                  createTableRow(t('headers.basicInfo.totalScientificCitations'), basicData.totalStats.scientificCitations),
                ],
              }),

              // Publications Section
              new Paragraph({
                text: t('headers.publications.Title'),
                heading: HeadingLevel.HEADING_2,
                spacing: { before: 400, after: 200 },
              }),

              ...publications.map(
                (pub) =>
                  new Paragraph({
                    children: [
                      ...headerSelections.word.map(header => {
                        const value = getPublicationData(pub, header, author.mtid, t);
                        const headerLabel = defaultHeaders.find(h => h.key === header)?.label;
                        
                        if (header === 'url' && value) {
                          return [
                            new TextRun({ text: `${headerLabel}: `, size: 20 }),
                            new ExternalHyperlink({
                              children: [
                                new TextRun({
                                  text: value.toString(),
                                  style: "Hyperlink",
                                  size: 20,
                                }),
                              ],
                              link: value.toString(),
                            }),
                            new TextRun({ text: "\n" }),
                          ];
                        }
                        
                        if (header === 'mtmtId' && value) {
                          const mtmtUrl = `https://m2.mtmt.hu/api/publication/${value}`;
                          return [
                            new TextRun({ text: `${headerLabel}: `, size: 20 }),
                            new ExternalHyperlink({
                              children: [
                                new TextRun({
                                  text: value.toString(),
                                  style: "Hyperlink",
                                  size: 20,
                                }),
                              ],
                              link: mtmtUrl,
                            }),
                            new TextRun({ text: "\n" }),
                          ];
                        }

                        return new TextRun({
                          text: `${headerLabel}: ${value}\n`,
                          size: 20,
                        });
                      }).flat(),
                      new TextRun({ text: "\n" }),
                    ],
                    spacing: { after: 200 },
                  })
              ),
            ],
          },
        ],
      });

      const blob = await Packer.toBlob(doc);
      saveAs(blob, getFileName("word"));
    } catch (error) {
      console.error("Error generating Word document:", error);
    }
  };

  return (
    <div className="relative">
      <div className="flex justify-end">
        <button
          ref={buttonRef}
          onClick={() => setShowExportOptions(!showExportOptions)}
          className="flex items-center gap-2 sm:gap-2 px-4 sm:px-4 py-2.5 sm:py-3 text-sm sm:text-sm font-medium text-white hover:text-gray-100 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-md cursor-pointer hover:scale-105"
        >
          <FaFileExport className="w-4 h-4 sm:w-4 sm:h-4" />
          {t('title')}
        </button>
      </div>

      {showExportOptions && (
        <div ref={menuRef} className="absolute top-full right-0 -mt-3 bg-white rounded-lg shadow-lg p-1 sm:p-2 z-50 flex flex-col min-w-[140px] sm:min-w-[160px] border border-gray-200">
          <button
            onClick={() => {
              setExportType('excel');
              setShowHeaderSelection(true);
              setShowExportOptions(false);
            }}
            className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
          >
            <PiMicrosoftExcelLogo className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
            {t('excel')}
          </button>
          <button
            onClick={() => {
              setExportType('word');
              setShowHeaderSelection(true);
              setShowExportOptions(false);
            }}
            className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
          >
            <PiMicrosoftWordLogo className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
            {t('word')}
          </button>
          <button
            onClick={() => {
              setExportType('csv');
              setShowHeaderSelection(true);
              setShowExportOptions(false);
            }}
            className="flex items-center gap-1.5 sm:gap-2 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm text-gray-600 hover:bg-gray-50 rounded-md transition-colors"
          >
            <FaFileCsv className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-500" />
            {t('csv')}
          </button>
        </div>
      )}

      {showHeaderSelection && (
        <div className="absolute top-full right-0 -mt-3 bg-white rounded-lg shadow-lg p-3 sm:p-4 z-50 w-[280px] sm:w-64 border border-gray-200">
          <div className="flex justify-between items-center mb-2 sm:mb-3">
            <h3 className="font-medium text-xs sm:text-sm text-gray-800">{t('selectHeaders')}</h3>
            <button
              onClick={() => setShowHeaderSelection(false)}
              className="text-gray-400 hover:text-gray-600 p-0.5 sm:p-1 rounded-full hover:bg-gray-50 transition-colors"
              aria-label="Close"
            >
              <IoClose className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
            </button>
          </div>
          
          <div className="flex gap-1.5 sm:gap-2 mb-2 sm:mb-3">
            <button
              onClick={() => {
                setHeaderSelections(prev => ({
                  ...prev,
                  [exportType]: defaultHeaders.map(h => h.key)
                }));
              }}
              className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            >
              {t('selectAll')}
            </button>
            <button
              onClick={() => {
                setHeaderSelections(prev => ({
                  ...prev,
                  [exportType]: []
                }));
              }}
              className="text-[10px] sm:text-xs px-1.5 sm:px-2 py-0.5 sm:py-1 bg-gray-100 hover:bg-gray-200 rounded-full transition-colors"
            >
              {t('selectNone')}
            </button>
          </div>

          <div className="space-y-1 sm:space-y-1.5 max-h-48 sm:max-h-64 overflow-y-auto mb-2 sm:mb-3 pr-1 sm:pr-2">
            {defaultHeaders.map((header) => (
              <label
                key={header.key}
                className="flex items-center space-x-1.5 sm:space-x-2 cursor-pointer hover:bg-gray-50 p-1 sm:p-1.5 rounded-lg transition-colors"
              >
                <input
                  type="checkbox"
                  checked={headerSelections[exportType].includes(header.key)}
                  onChange={() => {
                    const currentHeaders = headerSelections[exportType];
                    const newHeaders = currentHeaders.includes(header.key)
                      ? currentHeaders.filter(h => h !== header.key)
                      : [...currentHeaders, header.key];
                    setHeaderSelections(prev => ({
                      ...prev,
                      [exportType]: newHeaders
                    }));
                  }}
                  className="rounded text-bme-main focus:ring-bme-main w-3 h-3 sm:w-3.5 sm:h-3.5"
                />
                <span className="text-[10px] sm:text-xs text-gray-700">{header.label}</span>
              </label>
            ))}
          </div>

          <div className="absolute bottom-2 sm:bottom-3 left-2 sm:left-3 right-2 sm:right-3">
            <button
              onClick={() => {
                setShowHeaderSelection(false);
                if (exportType === 'excel') exportToExcel();
                else if (exportType === 'word') exportToWord();
                else exportToCSV();
              }}
              className="w-full flex items-center justify-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-1.5 sm:py-2 bg-bme-main hover:bg-bme-main text-white rounded-lg transition-all duration-200 hover:scale-[1.02] font-medium text-xs sm:text-sm"
            >
              <IoCheckmarkCircle className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
              {t('confirm')}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};